// Statistics over one city's typical year.
//
// Everything here is a pure function of a series (and the calendar keys that
// index it) — no DOM, no app state, no fetching. The renderers in city-view.ts
// and the client's city panel read their numbers from here.
//
// The timeline is passed in as `dates` (the section keys "MM-p" the open series
// is indexed by) rather than read off a global, so the same function serves the
// 36-section calendar and any other granularity a frame set is built at.

import { MONTH_NAMES, daysInSection } from './sections.ts';
import type { CitySeries, CityClimate } from '../contracts/city.ts';
import type { Factor } from './recipe.ts';
import type { VarKey } from './variables.ts';

// Month names at three lengths, all derived from the one list so a rename cannot
// leave the tablet track spelling a month differently from the desktop track.
export const MONTH_FULL = MONTH_NAMES;
export const MONTH_ABBR = MONTH_NAMES.map((m) => m.slice(0, 3));
export const MONTH_INITIAL = MONTH_NAMES.map((m) => m.slice(0, 1));

// A series row averaged into 12 calendar months; null for a month with no
// readings at all.
export function monthlyMeans(arr: (number | null)[] | undefined, dates: string[]): (number | null)[] {
  const sums: number[] = new Array(12).fill(0);
  const cnt: number[] = new Array(12).fill(0);
  dates.forEach((d, i) => {
    const m = Number(d.slice(0, 2)) - 1;
    const v = arr && arr[i];
    if (v != null) {
      sums[m] += v;
      cnt[m]++;
    }
  });
  return sums.map((s, m) => (cnt[m] ? s / cnt[m] : null));
}

// A city's climate by month, from its series at every time of day: the month's
// coolest and warmest bucket as the low and high, and the day's mean chance of
// rain and cloud. One bucket alone cannot say this — midday is the warm end of
// the day, and in the tropics the rain falls in the afternoon and at night.
export function climateByMonth(byBucket: CitySeries[], dates: string[]): CityClimate {
  const per = (v: string) => byBucket.map((s) => monthlyMeans(s.series[v], dates));
  const t = per('t'), rain = per('rain'), sky = per('sky'), ws = per('ws');
  const defined = (xs: (number | null)[]) => xs.filter((x): x is number => x != null);
  const r1 = (x: number) => Math.round(x * 10) / 10;
  const months = Array.from({ length: 12 }, (_, m) => {
    const ts = defined(t.map((b) => b[m]));
    const mean = (rows: (number | null)[][]) => { const xs = defined(rows.map((b) => b[m])); return xs.length ? r1(xs.reduce((a, b) => a + b, 0) / xs.length) : null; };
    return {
      lo: ts.length ? r1(Math.min(...ts)) : null,
      hi: ts.length ? r1(Math.max(...ts)) : null,
      rain: mean(rain),
      sky: mean(sky),
      ws: mean(ws),
    };
  });
  const all = defined(byBucket.flatMap((s) => s.series.t || []));
  return { months, mean: all.length ? r1(all.reduce((a, b) => a + b, 0) / all.length) : null };
}

// "May to October", or just "May" for a run of one. Months are 0-based and the
// run may wrap the year end (first 10, last 1 is November to February).
export function monthSpan(first: number, last: number): string {
  return first === last ? MONTH_FULL[first] : `${MONTH_FULL[first]} to ${MONTH_FULL[last]}`;
}

// The wet season, when the year has one: the unbroken run of months around the
// wettest whose chance of rain sits in the upper half of the year's range, and
// the matching run around the driest. Null where the year has no such shape —
// rain that hardly varies, or hardly falls — because "the rainy season runs
// from…" stated of London or of the Sahara is an invention.
export interface WetSeason { wet: [number, number]; dry: [number, number]; peak: number; driest: number; hi: number; lo: number }
export function wetSeason(rain: (number | null)[]): WetSeason | null {
  if (rain.length !== 12 || rain.some((v) => v == null)) return null;
  const r = rain as number[];
  const peak = extentIdx(r, (a, b) => a > b), driest = extentIdx(r, (a, b) => a < b);
  const hi = r[peak], lo = r[driest];
  // A swing of 25 points is a season whatever it starts from; a smaller one
  // counts only where the wet end is at least double the dry.
  if (hi - lo < 15 || (hi < 2 * lo && hi - lo < 25)) return null;
  const mid = (hi + lo) / 2;
  // Grow outwards from the extreme while the neighbours stay on its side of the
  // midpoint. Neither run can lap the year: the other extreme stops it.
  const run = (from: number, inside: (v: number) => boolean): [number, number] => {
    let a = from, b = from;
    while (inside(r[(a + 11) % 12])) a = (a + 11) % 12;
    while (inside(r[(b + 1) % 12])) b = (b + 1) % 12;
    return [a, b];
  };
  return { wet: run(peak, (v) => v >= mid), dry: run(driest, (v) => v < mid), peak, driest, hi, lo };
}

// The four meteorological seasons as month indices, winter first, for the
// hemisphere a latitude is in: December–February is winter in the north and
// summer in the south.
export function seasonsFor(lat: number): { name: string; months: [number, number, number] }[] {
  const names = lat < 0 ? ['Summer', 'Autumn', 'Winter', 'Spring'] : ['Winter', 'Spring', 'Summer', 'Autumn'];
  const spans: [number, number, number][] = [[11, 0, 1], [2, 3, 4], [5, 6, 7], [8, 9, 10]];
  const out = names.map((name, i) => ({ name, months: spans[i] }));
  // Winter first either way, so the list reads in the same order everywhere.
  return lat < 0 ? [out[2], out[3], out[0], out[1]] : out;
}

// Mean of the defined values, or null if there are none.
export function meanOf(arr: (number | null)[] | undefined): number | null {
  let s = 0;
  let n = 0;
  for (const v of arr || []) if (v != null) { s += v; n++; }
  return n ? s / n : null;
}

// Index of the extreme value under `cmp` (pass (a, b) => a > b for the maximum),
// or -1 when nothing is defined.
export function extentIdx(arr: (number | null)[] | undefined, cmp: (a: number, b: number) => boolean): number {
  let bi = -1;
  let bv: number | null = null;
  (arr || []).forEach((v, i) => {
    if (v == null) return;
    if (bv == null || cmp(v, bv)) { bv = v; bi = i; }
  });
  return bi;
}

// The plain-language verdict for an activity score out of 100.
export function scoreBand(v: number): string {
  return v < 35 ? 'Poor' : v < 55 ? 'Fair' : v < 80 ? 'Good' : 'Ideal';
}

// A centred moving average that wraps at the year boundary.
export function smoothCircular(arr: (number | null)[], win: number): (number | null)[] {
  const n = arr.length;
  const half = Math.floor(win / 2);
  const out: (number | null)[] = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    if (arr[i] == null) continue;
    let s = 0;
    let c = 0;
    for (let k = -half; k <= half; k++) {
      const v = arr[(i + k + n) % n];
      if (v != null) { s += v; c++; }
    }
    out[i] = c ? s / c : null;
  }
  return out;
}

// The daily normals carry day-to-day noise, so the year charts smooth them into a
// trend. Section frames (36 dekads) are ALREADY ~10-day means, so smoothing them
// would blur a quarter of the year — leave those raw. Which kind of timeline this
// is shows in its length, so the caller passes it rather than the decision.
export function trendSmooth(arr: (number | null)[], win: number, timelineLength: number): (number | null)[] {
  return timelineLength <= 60 ? arr : smoothCircular(arr, win);
}

// A section's cumulative rainfall (mm): the mean rain rate (mm/h, baked as
// `prate`) integrated over the dekad — rate × 24h × days-in-section. Null where
// the grid predates the prate field so the diagram simply draws empty rather
// than erroring.
export function cumulativeRainAt(s: CitySeries, i: number, dates: string[]): number | null {
  const rate = s.series.prate;
  return rate && rate[i] != null ? rate[i]! * 24 * daysInSection(dates[i]) : null;
}

export function cumulativeRain(s: CitySeries, dates: string[]): (number | null)[] {
  const rate = s.series.prate;
  if (!rate) return new Array(dates.length).fill(null);
  return rate.map((r, i) => (r == null ? null : r * 24 * daysInSection(dates[i])));
}

// The month an activity scores best in, and that month's mean score.
export function bestMonthFor(s: CitySeries, act: VarKey, dates: string[]): { month: string; score: number } | null {
  const mm = monthlyMeans(s.series[act], dates);
  const bi = extentIdx(mm, (a, b) => a > b);
  return bi >= 0 ? { month: MONTH_FULL[bi], score: Math.round(mm[bi]!) } : null;
}

// How one recipe factor reads at a measured value: inside its ideal range (✓),
// inside the tolerable range (~), or outside it (✗). The label follows the
// direction it missed in, so "too windy" and "too still" are distinguishable.
export function driverState(f: Factor, x: number): { cls: string; mark: string; text: string } {
  const within = (r: [number | null, number | null]) => (r[0] == null || x >= r[0]) && (r[1] == null || x <= r[1]);
  if (within(f.good)) return { cls: 'good', mark: '✓', text: f.label };
  const high = f.good[1] != null && x > f.good[1];
  const text = (high ? f.high : f.low) || f.label;
  return within(f.ok) ? { cls: 'mid', mark: '~', text } : { cls: 'bad', mark: '✗', text };
}

// The one driver holding a score back — the worst verdict among them, or null
// when nothing is even marginal.
//
// This is what the COLLAPSED score details say. "56 Good" on its own invites the
// question and does not answer it; "held back by wind" answers it in three words
// and tells a reader whether the full breakdown is worth opening. Ties go to the
// first factor, which is the gate — the thing the rating is actually about.
export function limitingFactor(
  factors: Factor[],
  at: (v: string) => number | null | undefined,
): { factor: Factor; cls: string; text: string } | null {
  let worst: { factor: Factor; cls: string; text: string } | null = null;
  for (const f of factors) {
    const x = at(f.v);
    if (x == null) continue;
    const st = driverState(f, Number(x));
    if (st.cls === 'good') continue;
    // 'bad' beats 'mid'; the first of equals wins, and the gate is first.
    if (!worst || (st.cls === 'bad' && worst.cls === 'mid')) worst = { factor: f, cls: st.cls, text: st.text };
  }
  return worst;
}

// "3.2 million" for the big ones, thousands-separated below that.
export function popLabel(pop: number | null | undefined): string {
  if (pop == null) return '';
  if (pop >= 1e6) return `${(pop / 1e6).toFixed(1)} million`;
  // Rounded to the nearest thousand. "27,134 people" claims a precision the
  // source does not have — these are gazetteer estimates of varying vintage, and
  // the last three digits are noise dressed as fact. Under a thousand the
  // rounding would BE the number, so those are left as they came.
  const shown = pop >= 1000 ? Math.round(pop / 1000) * 1000 : pop;
  return Number(shown).toLocaleString('en-GB');
}

// The place under a city name: "Pennsylvania, United States" where a state is
// carried (US only — the server strips admin elsewhere), else just the country.
export function placeLabel(country: string | undefined, admin: string | undefined): string {
  return admin ? `${admin}, ${country}` : country || '';
}
